/**
 * @file kitchenOps.service.ts
 * @description Dedicated service handling kitchen inventory 86ing, portion counts, load status, and whiteboard.
 * 
 * CORE RESPONSIBILITIES:
 * 1. Queries and updates item availability (IN_STOCK, LOW_STOCK, OUT_OF_STOCK) in Supabase.
 * 2. Increments/decrements remaining portion counts and flips item availability when depleted.
 * 3. Updates and persists venue kitchen load status (NORMAL, BUSY, RUSH, OVERLOAD).
 * 4. Syncs the daily kitchen whiteboard notes across all terminals.
 */

import { supabase } from '../../../config/supabase.config';
import type { 
  KitchenOpsItem, 
  StockStatus, 
  KitchenLoadStatus, 
  VenueKitchenState,
  KitchenWhiteboardData
} from '../types/kitchenOps.types';

/**
 * Fetches all menu items for the venue ordered by stock status and name.
 */
export const fetchKitchenOpsItems = async (venueId: string): Promise<KitchenOpsItem[]> => {
  const { data, error } = await supabase
    .from('items')
    .select('*')
    .eq('venue_id', venueId)
    .order('is_available', { ascending: true })
    .order('name', { ascending: true });

  if (error) {
    console.error('[kitchenOps.service] Error fetching items:', error);
    throw error;
  }

  return (data || []) as KitchenOpsItem[];
};

/**
 * Updates an item's stock status and synchronizes the boolean is_available flag.
 */
export const updateItemStockStatus = async (
  itemId: string, 
  status: StockStatus
): Promise<void> => {
  const isAvailable = status !== 'OUT_OF_STOCK';
  const { error } = await supabase
    .from('items')
    .update({
      stock_status: status,
      is_available: isAvailable,
    })
    .eq('id', itemId);

  if (error) {
    console.error('[kitchenOps.service] Error updating stock status:', error);
    throw error;
  }
};

/**
 * Updates remaining portions countdown for an item and updates stock_status and is_available accordingly.
 */
export const updateItemRemainingStock = async (
  item: KitchenOpsItem, 
  newStock: number
): Promise<{ newStatus: StockStatus; isAvailable: boolean; updatedSettings: any }> => {
  const currentSettings = (item.availability_settings as any) || {};
  const lowThreshold = currentSettings.low_stock_threshold ?? 5;
  const isOut = newStock <= 0;
  const isLow = newStock <= lowThreshold;
  const newStatus: StockStatus = isOut ? 'OUT_OF_STOCK' : isLow ? 'LOW_STOCK' : 'IN_STOCK';
  const isAvailable = !isOut;

  const updatedSettings = {
    ...currentSettings,
    remaining_stock: newStock,
  };

  const { error } = await supabase
    .from('items')
    .update({
      stock_status: newStatus,
      is_available: isAvailable,
      availability_settings: updatedSettings,
    })
    .eq('id', item.id);

  if (error) {
    console.error('[kitchenOps.service] Error updating remaining stock:', error);
    throw error;
  }

  return { newStatus, isAvailable, updatedSettings };
};

/**
 * Updates quick kitchen prep notes on a specific dish.
 */
export const updateItemInternalNotes = async (
  itemId: string, 
  notes: string
): Promise<void> => {
  const { error } = await supabase
    .from('items')
    .update({ internal_notes: notes })
    .eq('id', itemId);

  if (error) {
    console.error('[kitchenOps.service] Error updating item prep notes:', error);
    throw error;
  }
};

/**
 * Parses raw kitchen_whiteboard data safely into manager and chef note fields.
 * Supports legacy plain text strings by assigning them to managerNote.
 */
export const parseKitchenWhiteboard = (raw: string | null | undefined): KitchenWhiteboardData => {
  if (!raw) return { managerNote: '', chefNote: '' };
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null) {
      return {
        managerNote: typeof parsed.managerNote === 'string' ? parsed.managerNote : (typeof parsed.manager === 'string' ? parsed.manager : ''),
        chefNote: typeof parsed.chefNote === 'string' ? parsed.chefNote : (typeof parsed.chef === 'string' ? parsed.chef : ''),
      };
    }
  } catch {
    // Legacy plain string fallback: assign to managerNote
    return { managerNote: raw, chefNote: '' };
  }
  return { managerNote: '', chefNote: '' };
};

/**
 * Serializes manager and chef notes into a unified JSON string for venues.kitchen_whiteboard.
 */
export const serializeKitchenWhiteboard = (data: KitchenWhiteboardData): string => {
  return JSON.stringify({
    managerNote: data.managerNote || '',
    chefNote: data.chefNote || '',
  });
};

/**
 * Fetches the venue's active kitchen load status and dual whiteboard messages (manager + chef).
 */
export const fetchVenueKitchenState = async (venueId: string): Promise<VenueKitchenState> => {
  const { data, error } = await supabase
    .from('venues')
    .select('kitchen_load_status, kitchen_whiteboard')
    .eq('id', venueId)
    .single();

  if (error) {
    console.error('[kitchenOps.service] Error fetching venue kitchen state:', error);
    return {
      kitchen_load_status: 'NORMAL',
      managerNote: '',
      chefNote: '',
    };
  }

  const whiteboards = parseKitchenWhiteboard(data?.kitchen_whiteboard);

  return {
    kitchen_load_status: (data?.kitchen_load_status as KitchenLoadStatus) || 'NORMAL',
    managerNote: whiteboards.managerNote,
    chefNote: whiteboards.chefNote,
  };
};

/**
 * Updates the venue's operational kitchen load level (NORMAL / BUSY / RUSH / OVERLOAD).
 */
export const updateVenueKitchenLoadStatus = async (
  venueId: string, 
  status: KitchenLoadStatus
): Promise<void> => {
  const { error } = await supabase
    .from('venues')
    .update({ kitchen_load_status: status })
    .eq('id', venueId);

  if (error) {
    console.error('[kitchenOps.service] Error updating kitchen load status:', error);
    throw error;
  }
};

/**
 * Updates the chef's own whiteboard note while preserving the manager's note.
 */
export const updateVenueChefNote = async (
  venueId: string,
  currentManagerNote: string,
  chefNote: string
): Promise<void> => {
  const payload = serializeKitchenWhiteboard({
    managerNote: currentManagerNote,
    chefNote,
  });

  const { error } = await supabase
    .from('venues')
    .update({ kitchen_whiteboard: payload })
    .eq('id', venueId);

  if (error) {
    console.error('[kitchenOps.service] Error updating chef note:', error);
    throw error;
  }
};

/**
 * Updates the manager's whiteboard note while preserving the chef's note.
 */
export const updateVenueManagerNote = async (
  venueId: string,
  managerNote: string,
  currentChefNote: string
): Promise<void> => {
  const payload = serializeKitchenWhiteboard({
    managerNote,
    chefNote: currentChefNote,
  });

  const { error } = await supabase
    .from('venues')
    .update({ kitchen_whiteboard: payload })
    .eq('id', venueId);

  if (error) {
    console.error('[kitchenOps.service] Error updating manager note:', error);
    throw error;
  }
};

/**
 * Backward-compatible helper to update the raw kitchen whiteboard text.
 */
export const updateVenueKitchenWhiteboard = async (
  venueId: string, 
  text: string
): Promise<void> => {
  const { error } = await supabase
    .from('venues')
    .update({ kitchen_whiteboard: text })
    .eq('id', venueId);

  if (error) {
    console.error('[kitchenOps.service] Error updating whiteboard text:', error);
    throw error;
  }
};
